import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  useToast,
  Select,
  Switch,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  postDataSuperAdmin,
  updateDataSuperAdmin,
  getDataByIdSuperAdmin,
} from "../../../redux/actions/common.action";
import axios from "axios";
import { Home, ChevronRightIcon, Building2 } from "lucide-react";
import ImageUpload from "../../../helpers/ImageUpload";

interface Country {
  name: { common: string };
  cca2: string;
  flags: { svg: string };
}

const CreateFooterContent: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const toast = useToast();

  const { footerContent, loading } = useSelector((state: any) => state.common);

  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);

  const [formData, setFormData] = useState({
    companyName: "",
    companyDescription: "",
    officeName: "",
    address: "",
    country: {
      name: "",
      code: "",
      flag: "",
    },
    email: "",
    phone: "",
    companyLogo: "",
    socialMedia: {
      facebook: "",
      instagram: "",
      linkedin: "",
      twitter: "",
      youtube: "",
    },
    appLinks: {
      android: "",
      ios: "",
    },
    certificationBadge: "",
    isActive: true,
  });

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(
          "https://restcountries.com/v3.1/all?fields=name,flags,cca2"
        );
        const sortedCountries = response.data.sort((a: Country, b: Country) =>
          a.name.common.localeCompare(b.name.common)
        );
        setCountries(sortedCountries);
      } catch (error) {
        console.error("Error fetching countries:", error);
        toast({
          title: "Error",
          description: "Failed to load countries",
          status: "error",
          duration: 3000,
          position: "top-right",
        });
      }
    };

    fetchCountries();
  }, []);

  // Load existing data if editing
  useEffect(() => {
    if (id) {
      dispatch(getDataByIdSuperAdmin(id, "footer-content"));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (id && footerContent?.Data) {
      const data = footerContent.Data;
      setFormData({
        companyName: data.companyName || "",
        companyDescription: data.companyDescription || "",
        officeName: data.officeName || "",
        address: data.address || "",
        country: data.country || { name: "", code: "", flag: "" },
        email: data.email || "",
        phone: data.phone || "",
        companyLogo: data.companyLogo || "",
        socialMedia: data.socialMedia || {
          facebook: "",
          instagram: "",
          linkedin: "",
          twitter: "",
          youtube: "",
        },
        appLinks: data.appLinks || { android: "", ios: "" },
        certificationBadge: data.certificationBadge || "",
        isActive: data.isActive !== undefined ? data.isActive : true,
      });

      // Set selected country
      const country = countries.find(
        (c) => c.name.common === data.country?.name
      );
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [footerContent, id, countries]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof typeof prev] as any),
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryName = e.target.value;
    const country = countries.find((c) => c.name.common === countryName);

    if (country) {
      setSelectedCountry(country);
      setFormData((prev) => ({
        ...prev,
        country: {
          name: country.name.common,
          code: country.cca2,
          flag: country.flags.svg,
        },
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.companyName ||
      !formData.country.name ||
      !formData.officeName
    ) {
      toast({
        title: "Error",
        description: "Please fill all required fields",
        status: "error",
        duration: 3000,
        position: "top-right",
      });
      return;
    }

    try {
      if (id) {
        await dispatch(
          updateDataSuperAdmin(id, "footer-content", formData, navigate, toast)
        );
      } else {
        await dispatch(
          postDataSuperAdmin(formData, "footer-content", navigate, toast)
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <Box p="8px" bg="gray.50" minH="100vh">
      <style>
        {`
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .slide-in-left {
            animation: slideInLeft 0.4s ease-out;
          }
        `}
      </style>

      {/* Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
        p={4}
        className="slide-in-left"
      >
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <VStack align="start" spacing={2}>
            <Button
              leftIcon={<Home size={16} />}
              onClick={() => navigate("/footer-content-list")}
              size="sm"
              variant="ghost"
              colorScheme="blue"
              fontWeight="medium"
              fontSize="sm"
              px={3}
              _hover={{
                bg: "blue.50",
                transform: "translateX(-4px)",
              }}
              transition="all 0.2s"
            >
              Back to List
            </Button>

            <Breadcrumb
              spacing={2}
              separator={<ChevronRightIcon color="gray.400" size={12} />}
              fontSize="xs"
              color="gray.600"
            >
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() => navigate("/footer-content-list")}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ color: "blue.500" }}
                  transition="color 0.2s"
                >
                  <Home size={12} />
                  <Text>Footer Content</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink
                  display="flex"
                  alignItems="center"
                  gap={1}
                  color="blue.500"
                  fontWeight="medium"
                >
                  <Building2 size={12} />
                  <Text>{id ? "Edit" : "Create"}</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
          </VStack>

          <HStack spacing={4}>
            <VStack align="end" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                {id ? "Edit Footer Content" : "Create Footer Content"}
              </Text>
              <Text fontSize="xs" color="gray.500">
                Manage company footer information
              </Text>
            </VStack>
            <Box
              bgGradient="linear(to-br, orange.400, orange.600)"
              p={3}
              borderRadius="xl"
              boxShadow="md"
            >
              <Building2 size={20} color="white" />
            </Box>
          </HStack>
        </Flex>
      </Box>

      {/* Form */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        p={6}
      >
        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            {/* Company Information */}
            <Box>
              <Text fontSize="md" fontWeight="bold" mb={4} color="gray.800">
                Company Information
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Company Name</FormLabel>
                  <Input
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder="Enter company name"
                    size="sm"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Office Name</FormLabel>
                  <Input
                    name="officeName"
                    value={formData.officeName}
                    onChange={handleInputChange}
                    placeholder="e.g., Head Office, Sub Office"
                    size="sm"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="company@example.com"
                    size="sm"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Phone</FormLabel>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+91 1234567890"
                    size="sm"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired mt={4}>
                <FormLabel fontSize="sm">Company Description</FormLabel>
                <Textarea
                  name="companyDescription"
                  value={formData.companyDescription}
                  onChange={handleInputChange}
                  placeholder="Brief description about the company"
                  rows={3}
                  size="sm"
                />
              </FormControl>
            </Box>

            {/* Location Information */}
            <Box>
              <Text fontSize="md" fontWeight="bold" mb={4} color="gray.800">
                Location Information
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Country</FormLabel>
                  <Select
                    placeholder="Select country"
                    value={formData.country.name}
                    onChange={handleCountryChange}
                    size="sm"
                  >
                    {countries.map((country) => (
                      <option key={country.cca2} value={country.name.common}>
                        {country.name.common}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {selectedCountry && (
                  <Box>
                    <FormLabel fontSize="sm">Country Flag Preview</FormLabel>
                    <HStack>
                      <img
                        src={formData.country.flag}
                        alt={formData.country.name}
                        style={{ width: "40px", height: "30px" }}
                      />
                      <Text fontSize="sm" color="gray.600">
                        {formData.country.name} ({formData.country.code})
                      </Text>
                    </HStack>
                  </Box>
                )}
              </SimpleGrid>

              <FormControl isRequired mt={4}>
                <FormLabel fontSize="sm">Address</FormLabel>
                <Textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Complete office address"
                  rows={3}
                  size="sm"
                />
              </FormControl>
            </Box>

            {/* Images */}
            <Box>
              <Text fontSize="md" fontWeight="bold" mb={4} color="gray.800">
                Images
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Company Logo</FormLabel>
                  <ImageUpload
                    onImageUpload={(url) =>
                      setFormData((prev) => ({ ...prev, companyLogo: url }))
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Certification Badge</FormLabel>
                  <ImageUpload
                    onImageUpload={(url) =>
                      setFormData((prev) => ({
                        ...prev,
                        certificationBadge: url,
                      }))
                    }
                  />
                </FormControl>
              </SimpleGrid>
            </Box>

            {/* Social Media */}
            <Box>
              <Text fontSize="md" fontWeight="bold" mb={4} color="gray.800">
                Social Media Links
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">Facebook</FormLabel>
                  <Input
                    name="socialMedia.facebook"
                    value={formData.socialMedia.facebook}
                    onChange={handleInputChange}
                    placeholder="https://facebook.com/..."
                    size="sm"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Instagram</FormLabel>
                  <Input
                    name="socialMedia.instagram"
                    value={formData.socialMedia.instagram}
                    onChange={handleInputChange}
                    placeholder="https://instagram.com/..."
                    size="sm"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">LinkedIn</FormLabel>
                  <Input
                    name="socialMedia.linkedin"
                    value={formData.socialMedia.linkedin}
                    onChange={handleInputChange}
                    placeholder="https://linkedin.com/..."
                    size="sm"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">Twitter</FormLabel>
                  <Input
                    name="socialMedia.twitter"
                    value={formData.socialMedia.twitter}
                    onChange={handleInputChange}
                    placeholder="https://twitter.com/..."
                    size="sm"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">YouTube</FormLabel>
                  <Input
                    name="socialMedia.youtube"
                    value={formData.socialMedia.youtube}
                    onChange={handleInputChange}
                    placeholder="https://youtube.com/..."
                    size="sm"
                  />
                </FormControl>
              </SimpleGrid>
            </Box>

            {/* App Links */}
            <Box>
              <Text fontSize="md" fontWeight="bold" mb={4} color="gray.800">
                Mobile App Links
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">Android App Link</FormLabel>
                  <Input
                    name="appLinks.android"
                    value={formData.appLinks.android}
                    onChange={handleInputChange}
                    placeholder="https://play.google.com/..."
                    size="sm"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm">iOS App Link</FormLabel>
                  <Input
                    name="appLinks.ios"
                    value={formData.appLinks.ios}
                    onChange={handleInputChange}
                    placeholder="https://apps.apple.com/..."
                    size="sm"
                  />
                </FormControl>
              </SimpleGrid>
            </Box>

            {/* Active Status */}
            <FormControl display="flex" alignItems="center">
              <FormLabel fontSize="sm" mb={0}>
                Active Status
              </FormLabel>
              <Switch
                isChecked={formData.isActive}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isActive: e.target.checked,
                  }))
                }
                colorScheme="green"
              />
            </FormControl>

            {/* Submit Buttons */}
            <HStack spacing={4} justify="flex-end">
              <Button
                variant="outline"
                onClick={() => navigate("/footer-content-list")}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={loading}
                size="sm"
              >
                {id ? "Update" : "Create"} Footer Content
              </Button>
            </HStack>
          </VStack>
        </form>
      </Box>
    </Box>
  );
};

export default CreateFooterContent;
